import React, { useState, useCallback } from 'react'
import { FORM_ERROR } from 'final-form'
import PropTypes from 'prop-types'
import { track, segment } from '@mc/track-event'
import {
  LoginFormExperimentContainer,
  useLoginReminder,
} from '../../experiments/post-pw-ce-3448-last-login-reminder'
import {
  signIn,
  redirect,
  appleAuth,
  fbAuth,
  isSuccessful,
  needsConfirmation,
} from '../../services'

const LoginFormContainer = ({
  onConfirmationRequired,
  onLogIn,
  afterAuthAction,
  nextPage,
  ...restProps
}) => {
  const [thirdPartyError, setThirdPartyError] = useState('')

  const { isExperimentParticipant: isLoginReminderExperimentParticipant } =
    useLoginReminder()

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const res = await signIn({ ...values, nextPage })

        if (!isLoginReminderExperimentParticipant) {
          track(segment.EventTypes.SIGNED_IN, {
            location: 'modal',
          })
        }

        if (afterAuthAction) {
          afterAuthAction(res)
        }
        redirect(res, onLogIn)
        return {}
      } catch (error) {
        if (error.statusCode === 422) {
          if (error.status === 'sso_only_error') {
            const encodedEmail = encodeURIComponent(values.email)
            window.location.href = `/auth/auth0?login=prompt&login_hint=${encodedEmail}`
            return {}
          }
        } else if (error.statusCode === 429) {
          return {
            [FORM_ERROR]:
              'Due to multiple failed login attempts, your account has been temporarily locked for 2 hours.',
          }
        }
        return { [FORM_ERROR]: 'Your email or password are incorrect' }
      }
    },
    [nextPage, isLoginReminderExperimentParticipant, afterAuthAction, onLogIn],
  )

  const handleFacebookResponse = useCallback(
    (res) => {
      if (isSuccessful(res)) {
        if (afterAuthAction) {
          afterAuthAction(res, 'facebook')
        }

        redirect(res, onLogIn)
      } else if (needsConfirmation(res)) {
        onConfirmationRequired(res)
      }
    },
    [afterAuthAction, onLogIn, onConfirmationRequired],
  )

  const appleLogin = useCallback(
    (data) => {
      appleAuth(data)
        .then((res) => {
          redirect(res, onLogIn)
        })
        .catch((err) => {
          if (err.email) {
            setThirdPartyError(
              'You need to create an account on our iOS app first.',
            )
          } else {
            setThirdPartyError(
              'Sorry, something went wrong. Please try again or email support@masterclass.com.',
            )
          }
        })
    },
    [onLogIn],
  )

  const facebookLogin = useCallback(
    () =>
      fbAuth()
        .then(handleFacebookResponse)
        .catch((err) => {
          setThirdPartyError(err.message)
        }),
    [handleFacebookResponse],
  )

  return (
    <LoginFormExperimentContainer
      onSubmit={handleSubmit}
      facebookLogin={facebookLogin}
      appleLogin={appleLogin}
      thirdPartyError={thirdPartyError}
      onConfirmationRequired={onConfirmationRequired}
      onLogIn={onLogIn}
      afterAuthAction={afterAuthAction}
      nextPage={nextPage}
      {...restProps}
    />
  )
}

LoginFormContainer.propTypes = {
  onConfirmationRequired: PropTypes.func.isRequired,
  onLogIn: PropTypes.func,
  afterAuthAction: PropTypes.func,
  nextPage: PropTypes.string,
}

export default LoginFormContainer
